import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import ReactECharts from 'echarts-for-react';
import { api, type ArtikelHit } from '../lib/api.ts';
import { useFilterStore } from '../stores/filter-store.ts';
import { fmtCurrency, fmtCurrencyCompact, fmtNumber, fmtDate } from '../lib/format.ts';
import { COLORS, axisStyle, tooltipStyle } from '../lib/echarts-theme.ts';
import { Icon } from '../components/Icon.tsx';
import { Skeleton } from '../components/Skeleton.tsx';

const idLine = (a: ArtikelHit) =>
  [a.ean && `EAN ${a.ean}`, a.artikel_nr && `Mono ${a.artikel_nr}`, a.artikel_id && `DRS ${a.artikel_id}`]
    .filter(Boolean)
    .join('  ·  ');

export function ArtikelReportPage() {
  const filters = useFilterStore((s) => s.filters);
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selected, setSelected] = useState<ArtikelHit[]>([]);
  const [detailStore, setDetailStore] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const search = useQuery({
    queryKey: ['artikel-search', debounced],
    queryFn: () => api.artikelSearch(debounced),
    enabled: debounced.length >= 1,
  });

  const eans = useMemo(() => selected.map((s) => s.ean), [selected]);
  const report = useQuery({
    queryKey: ['artikel-report', eans, filters],
    queryFn: () => api.artikelReport(eans, filters),
    enabled: eans.length > 0,
  });

  const add = (a: ArtikelHit) => {
    setSelected((prev) => (prev.some((p) => p.ean === a.ean) ? prev : [...prev, a]));
    setQ('');
    setDebounced('');
  };
  const remove = (ean: string) => setSelected((prev) => prev.filter((p) => p.ean !== ean));

  const results = (search.data?.results ?? []).filter((r) => !selected.some((s) => s.ean === r.ean));

  return (
    <div className="space-y-6 w-full max-w-[1400px] mx-auto">
      {/* Kopf */}
      <div>
        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Artikel-Analyse</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Artikel suchen nach <b>Bezeichnung</b>, <b>EAN/PLU</b>, <b>Mono-Artikelnr</b> oder <b>DRS-Artikelnr</b> —
          ein oder mehrere wählen, dann Umsatz/Menge, Verlauf und Top-Filialen sehen.
          Zeitraum &amp; Filialen verfeinerst du links im Filter.
        </p>
      </div>

      {/* Suche */}
      <div className="card">
        <div className="card-body">
          <div className="relative">
            <Icon name="search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="z. B. Milch · EAN 4014… · Mono 20539061 · DRS 57962 …"
              className="w-full pl-10 pr-3 py-2.5 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 transition-all"
            />
          </div>

          {/* Such-Ergebnisse */}
          {debounced.length >= 1 && (
            <div className="mt-2 border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden divide-y divide-slate-100 dark:divide-slate-800 max-h-80 overflow-y-auto scrollbar-thin">
              {search.isLoading && <div className="p-3"><Skeleton height={18} /></div>}
              {!search.isLoading && results.length === 0 && (
                <div className="p-3 text-sm text-slate-400">Kein Artikel gefunden.</div>
              )}
              {results.map((r) => (
                <button
                  key={r.ean}
                  onClick={() => add(r)}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                      {r.name || '(ohne Bezeichnung)'}
                    </div>
                    <div className="text-2xs text-slate-400 font-mono truncate">{idLine(r)}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold tabular-nums text-slate-700 dark:text-slate-300">{fmtCurrency(r.umsatz)}</div>
                    <div className="text-2xs text-slate-400 tabular-nums">{fmtNumber(r.menge)} Stk</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Auswahl-Chips */}
          {selected.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {selected.map((s) => (
                <span key={s.ean} className="inline-flex items-center gap-2 pl-3 pr-1.5 py-1 rounded-full bg-brand-50 dark:bg-brand-950 text-brand-700 dark:text-brand-300 border border-brand-200/60 dark:border-brand-800 text-xs">
                  <span className="font-medium">{s.name || s.ean}</span>
                  <button onClick={() => remove(s.ean)} className="inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-brand-200 dark:hover:bg-brand-800" title="Entfernen">
                    <Icon name="x" size={11} />
                  </button>
                </span>
              ))}
              <button onClick={() => setSelected([])} className="text-2xs text-slate-400 hover:text-slate-600 px-2">alle entfernen</button>
            </div>
          )}
        </div>
      </div>

      {/* Leerzustand */}
      {selected.length === 0 && (
        <div className="text-center py-16 text-slate-400 dark:text-slate-500">
          <Icon name="table" size={28} className="mx-auto mb-3 opacity-50" />
          <p className="text-sm">Suche oben einen Artikel und wähle ihn aus, um die Auswertung zu sehen.</p>
        </div>
      )}

      {/* Bericht */}
      {selected.length > 0 && report.data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Kpi label="Umsatz" value={fmtCurrency(report.data.totals.umsatz)} />
            <Kpi label="Menge" value={`${fmtNumber(report.data.totals.menge)} Stk`} />
            <Kpi label="Bons mit Artikel" value={fmtNumber(report.data.totals.bons)} />
          </div>

          <div className="card">
            <div className="card-body">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">Umsatz-Verlauf (Monat)</h3>
              <MonthChart series={report.data.series} />
            </div>
          </div>

          <div className={`grid grid-cols-1 gap-4 ${selected.length > 1 ? 'lg:grid-cols-2' : ''}`}>
            <div className="card">
              <div className="card-body">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">Top-Filialen <span className="font-normal text-slate-400 text-xs">· Klick für Details</span></h3>
                <RankTable
                  rows={report.data.stores.map((s) => ({ label: s.store_name, umsatz: s.umsatz, menge: s.menge }))}
                  onRowClick={setDetailStore}
                />
              </div>
            </div>
            {selected.length > 1 && (
              <div className="card">
                <div className="card-body">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">Je Artikel</h3>
                  <RankTable rows={report.data.articles.map((a) => ({ label: a.name || a.ean, umsatz: a.umsatz, menge: a.menge }))} />
                </div>
              </div>
            )}
          </div>
        </>
      )}
      {selected.length > 0 && report.isLoading && <div className="card"><div className="card-body"><Skeleton height={120} /></div></div>}

      {detailStore && (
        <StoreDetailModal
          store={detailStore}
          eans={eans}
          filters={filters}
          onClose={() => setDetailStore(null)}
        />
      )}
    </div>
  );
}

function StoreDetailModal({
  store,
  eans,
  filters,
  onClose,
}: {
  store: string;
  eans: string[];
  filters: Record<string, unknown>;
  onClose: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['artikel-store-detail', store, eans, filters],
    queryFn: () => api.artikelStoreDetail(eans, store, filters),
  });

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="card-elevated w-full max-w-3xl my-auto animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-header flex items-center justify-between">
          <div>
            <h3 className="card-title">{store}</h3>
            <p className="card-subtitle">Verkäufe der gewählten Artikel · wann &amp; wie viel</p>
          </div>
          <button onClick={onClose} className="btn-icon text-slate-400 hover:text-slate-700" title="Schließen">
            <Icon name="x" size={18} />
          </button>
        </div>
        <div className="card-body space-y-5">
          {isLoading && <Skeleton height={260} />}
          {data && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <MiniKpi label="Umsatz" value={fmtCurrency(data.totals.umsatz)} />
                <MiniKpi label="Menge" value={`${fmtNumber(data.totals.menge)} Stk`} />
                <MiniKpi label="Bons" value={fmtNumber(data.totals.bons)} />
                <MiniKpi label="Verkaufstage" value={fmtNumber(data.totals.tage)} />
              </div>

              {data.daily.length > 0 ? (
                <div>
                  <div className="text-2xs uppercase tracking-wider font-medium text-slate-500 dark:text-slate-400 mb-2">
                    Verlauf {data.daily.length > 90 ? '(Monat)' : '(Tag)'}
                  </div>
                  <DetailChart
                    points={
                      data.daily.length > 90
                        ? data.monthly.map((m) => ({ x: m.zeitpunkt, umsatz: m.umsatz, menge: m.menge }))
                        : data.daily.map((d) => ({ x: d.tag, umsatz: d.umsatz, menge: d.menge }))
                    }
                  />
                </div>
              ) : (
                <div className="text-sm text-slate-400">Keine Verkäufe im gewählten Zeitraum.</div>
              )}

              {data.articles.length > 1 && (
                <div>
                  <div className="text-2xs uppercase tracking-wider font-medium text-slate-500 dark:text-slate-400 mb-2">Je Artikel</div>
                  <RankTable rows={data.articles.map((a) => ({ label: a.name || a.ean, umsatz: a.umsatz, menge: a.menge }))} />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MiniKpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-slate-50 dark:bg-slate-800/60 px-3 py-2">
      <div className="text-2xs uppercase tracking-wider font-medium text-slate-500 dark:text-slate-400">{label}</div>
      <div className="text-lg font-bold tabular-nums text-slate-900 dark:text-slate-100">{value}</div>
    </div>
  );
}

function DetailChart({ points }: { points: Array<{ x: string; umsatz: number; menge: number }> }) {
  const isDay = points[0]?.x?.length === 10; // YYYY-MM-DD
  const option = {
    grid: { top: 16, right: 16, bottom: 30, left: 60, containLabel: true },
    tooltip: {
      ...tooltipStyle(),
      trigger: 'axis',
      formatter: (params: Array<{ axisValue: string; value: number; data: { menge?: number } }>) => {
        const p = params[0];
        const head = isDay ? fmtDate(p.axisValue) : p.axisValue.replace('-', '/');
        const menge = p?.data?.menge;
        return `<div style="font-weight:600;margin-bottom:2px">${head}</div>
          <div style="font-size:12px">${fmtCurrency(p.value)}${menge != null ? ` · ${fmtNumber(menge)} Stk` : ''}</div>`;
      },
    },
    xAxis: {
      type: 'category',
      data: points.map((p) => p.x),
      ...axisStyle(),
      axisLabel: {
        ...axisStyle().axisLabel,
        formatter: (v: string) => (isDay && v.length === 10 ? `${v.slice(8, 10)}.${v.slice(5, 7)}` : v.replace('-', '/')),
      },
    },
    yAxis: { type: 'value', ...axisStyle(), axisLabel: { ...axisStyle().axisLabel, formatter: (v: number) => fmtCurrencyCompact(v) } },
    series: [
      {
        type: isDay ? 'line' : 'bar',
        data: points.map((p) => ({ value: p.umsatz, menge: p.menge })),
        smooth: true,
        showSymbol: false,
        sampling: 'lttb',
        barMaxWidth: 36,
        itemStyle: { color: COLORS.primary, borderRadius: isDay ? 0 : [4, 4, 0, 0] },
        lineStyle: { width: 2, color: COLORS.primary },
        areaStyle: isDay
          ? { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(31,151,245,0.22)' }, { offset: 1, color: 'rgba(31,151,245,0)' }] } }
          : undefined,
      },
    ],
  };
  return <ReactECharts option={option} style={{ height: 260 }} opts={{ renderer: 'canvas' }} notMerge={true} />;
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="card">
      <div className="card-body">
        <div className="text-2xs uppercase tracking-wider font-medium text-slate-500 dark:text-slate-400">{label}</div>
        <div className="text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-100 mt-1">{value}</div>
      </div>
    </div>
  );
}

function RankTable({
  rows,
  onRowClick,
}: {
  rows: Array<{ label: string; umsatz: number; menge: number }>;
  onRowClick?: (label: string) => void;
}) {
  const max = Math.max(1, ...rows.map((r) => r.umsatz));
  if (rows.length === 0) return <div className="text-sm text-slate-400">Keine Daten im gewählten Zeitraum.</div>;
  return (
    <div className="space-y-1.5">
      {rows.map((r) => {
        const inner = (
          <>
            <div className="absolute inset-y-0 left-0 bg-brand-50 dark:bg-brand-950/60 rounded-md" style={{ width: `${(r.umsatz / max) * 100}%` }} />
            <span className="relative text-sm text-slate-700 dark:text-slate-200 truncate flex items-center gap-1.5">
              {onRowClick && <Icon name="chevronRight" size={12} className="text-slate-400 shrink-0" />}
              {r.label}
            </span>
            <span className="relative text-sm font-semibold tabular-nums text-slate-900 dark:text-slate-100 shrink-0">
              {fmtCurrency(r.umsatz)} <span className="text-2xs font-normal text-slate-400">· {fmtNumber(r.menge)} Stk</span>
            </span>
          </>
        );
        return onRowClick ? (
          <button
            key={r.label}
            onClick={() => onRowClick(r.label)}
            className="w-full relative flex items-center justify-between gap-3 px-2.5 py-1.5 rounded-md overflow-hidden text-left hover:ring-1 hover:ring-brand-300 dark:hover:ring-brand-700 transition-all cursor-pointer"
          >
            {inner}
          </button>
        ) : (
          <div key={r.label} className="relative flex items-center justify-between gap-3 px-2.5 py-1.5 rounded-md overflow-hidden">
            {inner}
          </div>
        );
      })}
    </div>
  );
}

function MonthChart({ series }: { series: Array<{ zeitpunkt: string; umsatz: number; menge: number }> }) {
  if (series.length === 0) return <div className="text-sm text-slate-400">Keine Daten im gewählten Zeitraum.</div>;
  const option = {
    grid: { top: 16, right: 20, bottom: 30, left: 64, containLabel: true },
    tooltip: {
      ...tooltipStyle(),
      trigger: 'axis',
      formatter: (params: Array<{ axisValue: string; value: number; data: { menge?: number } }>) => {
        const p = params[0];
        const menge = p?.data?.menge;
        return `<div style="font-weight:600;margin-bottom:2px">${p.axisValue.replace('-', '/')}</div>
          <div style="font-size:12px">${fmtCurrency(p.value)}${menge != null ? ` · ${fmtNumber(menge)} Stk` : ''}</div>`;
      },
    },
    xAxis: { type: 'category', data: series.map((s) => s.zeitpunkt), ...axisStyle(),
      axisLabel: { ...axisStyle().axisLabel, formatter: (v: string) => v.replace('-', '/') } },
    yAxis: { type: 'value', ...axisStyle(), axisLabel: { ...axisStyle().axisLabel, formatter: (v: number) => fmtCurrencyCompact(v) } },
    series: [{
      type: 'bar',
      data: series.map((s) => ({ value: s.umsatz, menge: s.menge })),
      barMaxWidth: 36,
      itemStyle: { color: COLORS.primary, borderRadius: [4, 4, 0, 0] },
    }],
  };
  return <ReactECharts option={option} style={{ height: 300 }} opts={{ renderer: 'canvas' }} notMerge={true} />;
}
